import React, { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, HelpCircle, Check } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePreferences } from '@/contexts/PreferencesContext';

interface ConflictPreferences {
  defaultAction: 'preserve_manual' | 'accept_system' | 'preserve_unless_better';
  notificationLevel: 'summary';
  measureSpecificOverrides: Record<string, string>;
}

export function ConflictPreferencesCard() {
  const { toast } = useToast();
  const { conflictPreferences, loading, updatePreferences } = usePreferences();
  const [saving, setSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const savedTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [localPreferences, setLocalPreferences] = useState<ConflictPreferences>(
    conflictPreferences || {
      defaultAction: 'preserve_unless_better',
      notificationLevel: 'summary',
      measureSpecificOverrides: {},
    },
  );

  React.useEffect(() => {
    if (conflictPreferences) {
      setLocalPreferences({
        ...conflictPreferences,
        notificationLevel: 'summary',
      });
    }
  }, [conflictPreferences]);

  const autoSavePreferences = async (newPrefs: ConflictPreferences) => {
    setSaving(true);
    setShowSaved(false);

    if (savedTimeoutRef.current) {
      clearTimeout(savedTimeoutRef.current);
    }

    try {
      await updatePreferences(newPrefs);
      setShowSaved(true);
      savedTimeoutRef.current = setTimeout(() => {
        setShowSaved(false);
      }, 2000);
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast({
        title: 'Save Failed',
        description: 'Could not save preferences. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDefaultActionChange = (value: string) => {
    const newPrefs: ConflictPreferences = {
      ...localPreferences,
      defaultAction: value as ConflictPreferences['defaultAction'],
      notificationLevel: 'summary',
    };
    setLocalPreferences(newPrefs);
    autoSavePreferences(newPrefs);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Data Conflict Resolution</CardTitle>
          <CardDescription>Loading preferences...</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Data Conflict Resolution
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>Choose how to handle conflicts when new system calculations differ from your manual edits</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
        <CardDescription>
          Configure how the system should automatically resolve conflicts between new data processing results and your manual edits.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div>
          <div className="flex items-center justify-between mb-4">
            <Label className="text-base font-medium">Default Action</Label>
            <div className="flex items-center gap-2 h-5">
              {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              {showSaved && !saving && (
                <span className="flex items-center gap-1 text-sm text-green-600">
                  <Check className="h-4 w-4" />
                  Saved
                </span>
              )}
            </div>
          </div>
          <RadioGroup
            value={localPreferences.defaultAction}
            onValueChange={handleDefaultActionChange}
            disabled={saving}
          >
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="preserve_manual" id="preserve" className="mt-1" />
                <div className="space-y-1">
                  <Label htmlFor="preserve" className="font-medium cursor-pointer">Always preserve my manual edits</Label>
                  <p className="text-sm text-muted-foreground">Keep your manual changes even when new system calculations suggest different outcomes. Safest option for maintaining your clinical decisions.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <RadioGroupItem value="accept_system" id="accept" className="mt-1" />
                <div className="space-y-1">
                  <Label htmlFor="accept" className="font-medium cursor-pointer">Always accept new system calculations</Label>
                  <p className="text-sm text-muted-foreground">Replace your manual edits with new system calculations when they differ. Best for trusting automated processing.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <RadioGroupItem value="preserve_unless_better" id="smart" className="mt-1" />
                <div className="space-y-1">
                  <Label htmlFor="smart" className="font-medium cursor-pointer">Preserve manual edits unless the new data results in an improved outcome</Label>
                  <p className="text-sm text-muted-foreground">Keep your edits unless the new calculation provides a clinically superior outcome (Met &gt; Exception &gt; Not Met for normal measures).</p>
                </div>
              </div>
            </div>
          </RadioGroup>
        </div>
      </CardContent>
    </Card>
  );
}
